<template>

  <div
    class="vpay__step"
    v-loading="loading"
  >

    <div class="vpay__input-container">
      <label>What do you want to give towards?</label>
      <Dropdown
        :options="$store.getters.causes"
        :selected-option="$store.getters.selectedCause"
        :selected-option-title-text="$store.getters.selectedCause.title"
        placeholder-text="Select Cause"
      >
        <template v-slot:default="{ options, selected }">
          <DropdownOption
            v-for="(option, index) in options"
            :title="option.title"
            :subtitle="option | formatCauseCost"
            :selected="option.public_id === selected.public_id"
            :key="index"
            @click.native="onSelectCause(option)"
          />
        </template>
      </Dropdown>
    </div>

    <div
      v-if="notes"
      class="vpay__input-container"
    >
      <input
        class="vpay__input vpay__input--small"
        v-model="$store.state.notes"
        :placeholder="notesPlaceholder"
        @keypress="isLetter($event)"
      />
      <p
        v-if="invalidSymbol"
        class="vpay__input-note"
      >This symbol is not permitted</p>
    </div>

    <div class="vpay__input-container">
      <label>How much do you want to give?</label>
      <vue-numeric
        class="vpay__input"
        :style="inputStyle"
        currency="$"
        separator=","
        :precision="2"
        v-model="$store.state.amount"
        :min="minimumAmount"
        :max="maximumAmount"
        :read-only="!amountEditable"
        read-only-class="vpay__input"
      ></vue-numeric>
      <p
        v-if="causeDescription"
        class="vpay__input-note"
      >{{ causeDescription }}</p>
      <div
        v-if="userCanPayTheFees"
        style="display: block; text-align: center; margin-top: 8px;"
      >
        <el-checkbox
          v-model="$store.state.userWillPayTheFees"
          :true-label="1"
          :false-label="0"
        >{{ creditCardFeesDescription }}</el-checkbox>
      </div>
      <p
        v-if="userMustPayTheFees"
        class="vpay__input-note"
      >{{ creditCardFeesDescription }}</p>
    </div>

    <PaymentFrequency />

    <div
      v-if="$store.getters.embed || showNextButton"
      class="vpay__input-container"
    >
      <el-button
        ref="nextButton"
        class="vpay__next-button"
        @click="onClickNext"
        round
      >Next</el-button>
    </div>

    <div
      v-if="!$store.getters.embed && showAccountButtons"
      class="vpay__input-container"
      style="text-align: center;"
    >
      <p class="powered-by">To proceed with your payment, please:</p>
      <br>
      <el-button
        size="small"
        type="primary"
        @click="onClickLogInButton"
        round
      >Log In</el-button>
      <el-button
        size="small"
        type="primary"
        @click="onClickCreateAccountButton"
        round
      >Create Account</el-button>
    </div>

  </div>

</template>

<script>
import VueNumeric from "vue-numeric";

import PaymentFrequency from "@/components/PaymentFrequency";

import { Dropdown, DropdownOption } from "@/components/Dropdown";

export default {
  name: "PaymentFormStep2",

  components: { VueNumeric, PaymentFrequency, Dropdown, DropdownOption },

  methods: {
    onSelectCause(cause) {
      this.$store.commit("selectedCause", cause);
      if (cause.pwyw) {
        this.$store.commit("amount", 0);
        this.minimumAmount = cause.minimum_amount;
        this.amountEditable = true;
      } else {
        this.$store.commit("amount", cause.amount);
        this.minimumAmount = cause.minimum_amount;
        this.amountEditable = false;
      }
    },

    onClickNext() {
      if (
        this.$store.getters.selectedCause.id &&
        this.$store.getters.amount > 0
      ) {
        if (
          this.$store.getters.mobileNumberValidated &&
          this.$store.getters.isMobileNumberValidated
        ) {
          this.$store.commit("incrementPaymentStep");
        }
        this.$store.commit("incrementPaymentStep");
      } else {
        this.$errors.show(this.$refs.nextButton.$el, {
          message:
            "Please select what you are giving to and how much you are giving.",
        });
      }
    },

    onClickLogInButton() {
      this.$store.commit("displayComponentName", "LogIn");
    },

    onClickCreateAccountButton() {
      this.$store.commit("displayComponentName", "CreateAccount");
    },

    isLetter(e) {
      let char = String.fromCharCode(e.keyCode);
      if (
        char == "&" ||
        char == "?" ||
        char == "=" ||
        char == "/" ||
        char == "\\"
      ) {
        this.invalidSymbol = true;
        e.preventDefault();
      } else {
        this.invalidSymbol = false;
      }
    },
  },

  computed: {
    showNextButton() {
      return (
        this.$store.state.frequency.type === "one_time" ||
        this.$store.getters.userIsLoggedIn
      );
    },

    showAccountButtons() {
      return (
        this.$store.state.frequency.type === "recurring" &&
        !this.$store.getters.userIsLoggedIn
      );
    },

    clientPaysTheFees() {
      return this.$store.getters.selectedCause.who_pays_fees === 0;
    },

    userCanPayTheFees() {
      return this.$store.getters.selectedCause.who_pays_fees === 1;
    },

    userMustPayTheFees() {
      return this.$store.getters.selectedCause.who_pays_fees === 2;
    },

    notes() {
      return this.$store.getters.selectedCause.has_notes;
    },

    notesPlaceholder() {
      return this.$store.getters.selectedCause.notes_placeholder;
    },

    creditCardFeesDescription() {
      switch (this.$store.getters.selectedCause.who_pays_fees) {
        case 1:
          return `I will pay the credit card fee of ${this.$options.filters.formatAmount(
            this.$store.getters.creditCardFee
          )}`;
        case 2:
          return `A credit card fee of ${this.$options.filters.formatAmount(
            this.$store.getters.creditCardFee
          )} applies`;
        default:
          return "";
      }
    },

    causeDescription() {
      let description = "";

      if (this.$store.getters.selectedCause.hasOwnProperty("id")) {
        if (this.$store.getters.selectedCause.pwyw) {
          if (this.$store.state.amount === 0) {
            description = `Minimum donation amount of ${this.$options.filters.formatAmount(
              this.$store.getters.selectedCause.minimum_amount
            )}`;
          }
        } else {
          description = `Fixed donation amount of ${this.$options.filters.formatAmount(
            this.$store.getters.selectedCause.amount
          )}`;
        }
      }

      return description;
    },

    inputStyle() {
      return {
        "border-color": this.$store.getters.brandColour,
        color: this.$store.getters.brandColour,
      };
    },
  },

  mounted() {
    var that = this;

    this.loading = true;

    this.axios
      .get("donation_causes")
      .then((r) => {
        if (r.data.success) {
          if (that.$store.getters.donationCauses.length > 0) {
            var donation_causes = [];

            r.data.results.forEach(function (item) {
              if (
                that.$store.getters.donationCauses.indexOf(
                  item["public_id"]
                ) !== -1
              ) {
                donation_causes.push(item);
              }
            });

            that.$store.commit("causes", donation_causes);
          } else {
            that.$store.commit("causes", r.data.results);
          }
        }
      })
      .finally(() => {
        this.loading = false;
      });
  },

  data() {
    return {
      loading: false,

      minimumAmount: 0,
      maximumAmount: 6000,
      amountEditable: true,
      invalidSymbol: false,
    };
  },
};
</script>

<style>
</style>
